import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { CoreModule } from '@abp/ng.core';
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { conditionalValidator, DwApiSelectDirective } from '@digiwall/core';
import { SharedModule } from '@digiwall/goliath-core';
import {
  DocRole,
  PaymentTermLookupDto,
  PaymentTermService,
  StorageLocationLookupRequestDto,
  StorageLocationService,
} from '@digiwall/goliath-core/proxy';
import {
  CustomerReturnNoteService,
  CustomerReturnRequestDto,
  DayBookDto,
  DayBookService,
} from '@digiwall/goliath-documents/proxy';
import { StockFeatureDefinition } from '@digiwall/goliath-products/config';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import { mergeMap } from 'rxjs';

@Component({
  selector: 'document-customer-return-note-creation',
  templateUrl: './customer-return-note-creation.component.html',
  imports: [
    DialogBoxComponent,
    CommonModule,
    CoreModule,
    ReactiveFormsModule,
    SharedModule,
    DwApiSelectDirective,
  ],
})
export class CustomerReturnNoteCreationComponent implements OnInit {
  protected readonly stockFeature = inject(StockFeatureDefinition);
  private readonly formBuilder = inject(FormBuilder);
  private readonly daybookService = inject(DayBookService);
  private readonly storageLocationService = inject(StorageLocationService);
  private readonly paymentTermService = inject(PaymentTermService);
  private readonly returnNoteService = inject(CustomerReturnNoteService);
  private readonly activeCanvas = inject(NgbActiveOffcanvas);

  public returnNoteRequest: CustomerReturnRequestDto;

  protected form: FormGroup;
  protected daybooks: DayBookDto[] = [];
  protected paymentTerms: PaymentTermLookupDto[];

  ngOnInit(): void {
    this.initForm();

    this.daybookService
      .get(this.returnNoteRequest.dayBookId)
      .pipe(
        mergeMap(daybook => {
          return this.daybookService.getDayBooksByRole(
            DocRole.CustomerReturnNote,
            false,
            daybook.dayBookGroupId,
          );
        }),
      )
      .subscribeOnce(daybooks => {
        this.daybooks = daybooks;
        this.form.get('daybookId').setValue(this.daybooks[0]?.id);
      });

    this.getPaymentTerms();
  }

  protected createReturnNote() {
    if (!this.form.valid) return;

    const creationRequest = Object.assign(
      {
        returnRequestId: this.returnNoteRequest.id,
      },
      this.form.getRawValue(),
    );

    this.returnNoteService.createFromReturnNoteRequest(creationRequest).subscribeOnce(dto => {
      this.activeCanvas.close(dto.result);
    });
  }

  protected storageLocationSelect = (dto: StorageLocationLookupRequestDto) => {
    dto.sorting = [{ property: 'name' }];
    dto.queryProperties = ['code', 'name'];
    dto.companySiteId = this.returnNoteRequest.companySiteId;
    dto.onlyActive = true;
    return this.storageLocationService.getLookupList(dto);
  };

  private initForm() {
    this.form = this.formBuilder.group({
      daybookId: [null, Validators.required],
      storageLocationId: [
        null,
        conditionalValidator(() => {
          return this.stockFeature.isEnabled;
        }, Validators.required),
      ],
      description: [this.returnNoteRequest.description],
      paymentTermId: [this.returnNoteRequest.paymentTermId, Validators.required],
    });
  }

  private getPaymentTerms() {
    this.paymentTermService
      .getPaymentTermLookup()
      .subscribeOnce(paymentTerms => (this.paymentTerms = paymentTerms.items));
  }
}
